/**
 * @file 本だなの「教養」の区画を、分野のまとまりに分ける。
 *
 * @what: 分野の語彙 (id と表示名、既定の並び) をここに持ち、教養の区画に入った資料をまとまりに分けて返す。
 *   まなぶ人が決めた並び (api/shelfSettings.ts の subject_order) があればそれを先にし、並びに無い分野は
 *   既定の順で後ろに続ける。並び替えの操作 (1つ上へ / 下へ) も純関数として持つ。
 *   取り込んだときの役割が教養の本は資料に付いた分野 (subject) で分け、分野の無い本は「そのほか」に入る。
 *   まなぶ人の計画に載っていないために教養へ落ちた教材は、分野ではなく載っているカリキュラムの名前で
 *   まとめ、カリキュラムにも載っていない教材は「ほかの教材」に入る。まとまりの中は題名の順に並べ、
 *   別冊は本体の直後に置く。
 * @why: 教養の本が増えて1つの区画に何十冊も並び、資料 id の順では探す先が決まらない (代表の指摘、
 *   2026-09-17)。分野は本に付く性質でまなぶ人によらないので資料に1つ持ち、語彙はサーバでなく web が
 *   持つ (領域 realm と同じ扱い)。計画から落ちた教材に分野を付けないのは、その本はカリキュラムの名前で
 *   十分に見分けられ、教養の本と同じ分野に混ぜると「計画に入っていない」ことが見えなくなるためである。
 * @failure: 副作用を持たない純関数である。語彙に無い分野 id は「そのほか」に入る (棚から消えない)。
 */

import type { StudySource } from "../api/sources";

export interface ShelfSubject {
  id: string;
  label: string;
}

/** 分野の語彙である。並びがそのまま区画の中の並びになる (世界遺産・地理と哲学・思想は末尾に置く。代表の要望、2026-09-17)。id を変えると資料に付けた分野と切れるので変えない。 */
export const SHELF_SUBJECTS: readonly ShelfSubject[] = [
  { id: "art_color", label: "美術・色彩" },
  { id: "math_science", label: "数学・理科" },
  { id: "computing", label: "コンピュータ" },
  { id: "medicine", label: "医学" },
  { id: "creative", label: "創作・表現" },
  { id: "learning", label: "学び方・仕事" },
  { id: "heritage_geography", label: "世界遺産・地理" },
  { id: "philosophy", label: "哲学・思想" },
];

/** 分野の無い教養の本のまとまりを指す id である。 */
export const SHELF_SUBJECT_OTHER_ID = "other";
/** カリキュラムに載っていない教材のまとまりを指す id である。 */
export const SHELF_UNPLANNED_ID = "unplanned";

const OTHER_LABEL = "そのほか";
const UNPLANNED_LABEL = "ほかの教材";

export function shelfSubjectLabel(id: string): string | undefined {
  return SHELF_SUBJECTS.find((s) => s.id === id)?.label;
}

/**
 * まなぶ人の並びを当てた分野の一覧を返す。
 *
 * @what: order にある id を語彙にあるものだけその順に並べ、残りの分野を既定の順で後ろに続ける。
 *   語彙に無い id と重複は読み飛ばす。order が空なら既定の並びそのものになる。
 */
export function orderedShelfSubjects(order: readonly string[]): ShelfSubject[] {
  const out: ShelfSubject[] = [];
  const used = new Set<string>();
  for (const id of order) {
    if (used.has(id)) continue;
    const subject = SHELF_SUBJECTS.find((s) => s.id === id);
    if (!subject) continue;
    out.push(subject);
    used.add(id);
  }
  for (const subject of SHELF_SUBJECTS) {
    if (!used.has(subject.id)) out.push(subject);
  }
  return out;
}

/**
 * 分野を、見えている分野の1つ上 (前) か下 (後) へ動かした並びを返す。
 *
 * @what: visible は画面に出ている分野 id の並び (本のある分野だけ) で、id の隣にある見えている分野を
 *   相手にし、全分野の並び (orderedShelfSubjects で既定を補ったもの) の中で id をその相手の前 (上へ) か
 *   後 (下へ) に移す。全分野の id を並べて返す。端にあって相手が無いとき、知らない id のときは、
 *   補った並びをそのまま返す。visible を省くと全分野が見えているものとして隣と入れ替える。
 * @why: 相手を全分野の隣にすると、本の無い分野と入れ替わるだけで画面が変わらない押し方が生まれる。
 *   保存する並びは全分野を含める。※なぜなら一部だけ持つと、並びに無い分野が既定の順で後ろに続く
 *   規則と組み合わさって、押した結果と保存した結果が食い違うためである。
 */
export function moveShelfSubject(
  order: readonly string[],
  id: string,
  direction: "up" | "down",
  visible?: readonly string[],
): string[] {
  const ids = orderedShelfSubjects(order).map((s) => s.id);
  const shown = visible ?? ids;
  const at = shown.indexOf(id);
  const neighbor = shown[direction === "up" ? at - 1 : at + 1];
  if (at < 0 || neighbor === undefined || !ids.includes(id) || !ids.includes(neighbor)) return ids;
  const rest = ids.filter((x) => x !== id);
  const target = rest.indexOf(neighbor) + (direction === "up" ? 0 : 1);
  rest.splice(target, 0, id);
  return rest;
}

/** 区画に置く資料1点と、その本 (別冊なら本体)、本が載っているカリキュラムの短い名前を持つ。 */
export interface ShelfEntry {
  source: StudySource;
  /** 別冊なら本体、そうでなければ source 自身を指す。分野と役割はこちらで見る。 */
  book: StudySource;
  curriculums?: readonly string[];
}

export interface ShelfGroup {
  id: string;
  label: string;
  sources: StudySource[];
}

const collator = new Intl.Collator("ja");

/** 題名の順に並べ、別冊は本体の直後に置く。 */
function sortEntries(entries: ShelfEntry[]): StudySource[] {
  return [...entries]
    .sort((a, b) => {
      const byBook = collator.compare(a.book.title, b.book.title);
      if (byBook !== 0) return byBook;
      const aCompanion = a.source !== a.book ? 1 : 0;
      const bCompanion = b.source !== b.book ? 1 : 0;
      if (aCompanion !== bCompanion) return aCompanion - bCompanion;
      return collator.compare(a.source.title, b.source.title);
    })
    .map((e) => e.source);
}

/**
 * 教養の区画の資料をまとまりに分ける。
 *
 * @what: 分野 (まなぶ人の並び、無ければ語彙の順)、そのほか、カリキュラムの名前のまとまり (名前の順)、
 *   ほかの教材 の順に並べる。空のまとまりは返さない。
 */
export function groupCultureShelf(
  entries: readonly ShelfEntry[],
  subjectOrder: readonly string[] = [],
): ShelfGroup[] {
  const bySubject = new Map<string, ShelfEntry[]>();
  const other: ShelfEntry[] = [];
  const byCurriculum = new Map<string, ShelfEntry[]>();
  const unplanned: ShelfEntry[] = [];
  for (const e of entries) {
    if (e.book.kind === "culture") {
      const subject = e.book.subject;
      if (subject && shelfSubjectLabel(subject) !== undefined) {
        const list = bySubject.get(subject) ?? [];
        list.push(e);
        bySubject.set(subject, list);
      } else {
        other.push(e);
      }
      continue;
    }
    if (e.curriculums && e.curriculums.length > 0) {
      const key = e.curriculums.join("・");
      const list = byCurriculum.get(key) ?? [];
      list.push(e);
      byCurriculum.set(key, list);
    } else {
      unplanned.push(e);
    }
  }

  const out: ShelfGroup[] = [];
  for (const subject of orderedShelfSubjects(subjectOrder)) {
    const list = bySubject.get(subject.id);
    if (list && list.length > 0) {
      out.push({ id: subject.id, label: subject.label, sources: sortEntries(list) });
    }
  }
  if (other.length > 0) {
    out.push({ id: SHELF_SUBJECT_OTHER_ID, label: OTHER_LABEL, sources: sortEntries(other) });
  }
  for (const name of [...byCurriculum.keys()].sort((a, b) => collator.compare(a, b))) {
    out.push({
      id: `curriculum:${name}`,
      label: `${name}の本`,
      sources: sortEntries(byCurriculum.get(name) ?? []),
    });
  }
  if (unplanned.length > 0) {
    out.push({ id: SHELF_UNPLANNED_ID, label: UNPLANNED_LABEL, sources: sortEntries(unplanned) });
  }
  return out;
}
